/*
 * Сбор: что именно страница сообщает о себе.
 *
 * Правило одно и оно жёсткое: отсюда уходит только то, что перечислено в
 * `.agent/docs/ANALYTICS_EVENT_REGISTRY.md`. Ни одного поля «на всякий случай».
 * Приёмная точка всё равно отбросит лишнее, но полагаться на это как на
 * оправдание нельзя: первый рубеж здесь.
 *
 * Чего здесь нет и не будет: движений мыши, нажатий клавиш, содержимого полей,
 * полных адресов переходов, почты, имени — ничего из того, что человек вводит.
 */

import type { AnalyticsEvent, Transport } from "./transport";
import { SECTION_IDS } from "../marketing/types";
import { CHAPTERS } from "../marketing/cinema/transport";

/* То же, чем на самом деле является `window`: интерфейс плюс глобальные
 * построители. Без этого `IntersectionObserver` у типа `Window` не виден. */
type View = Window & typeof globalThis;

export type Device = "mobile" | "desktop";

/*
 * Семейство браузера, а не его версия и не полная строка представления.
 * Порядок проверок важен: браузеры на общем движке честно называют себя
 * следом за ним, и «Chrome» встречается в строке почти у всех.
 */
export function browserFamily(userAgent: string): string {
  const ua = userAgent;
  if (/YaBrowser/i.test(ua)) return "yandex";
  if (/SamsungBrowser/i.test(ua)) return "samsung";
  if (/\bOPR\/|\bOpera\b/i.test(ua)) return "opera";
  if (/\bEdg\//i.test(ua)) return "edge";
  if (/Firefox\/|FxiOS/i.test(ua)) return "firefox";
  if (/Chrome\/|CriOS/i.test(ua)) return "chrome";
  if (/Safari\//i.test(ua)) return "safari";
  return "other";
}

/** Граница та же, что у всего сайта: до 700 — телефон. */
export function deviceKind(width: number): Device {
  return width < 700 ? "mobile" : "desktop";
}

const UTM_FIELDS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

/*
 * Метки перехода — только из адреса, без догадок. Имя хоста, откуда пришли,
 * берётся без пути и без параметров: в них попадают личные адреса писем и
 * поисковые запросы, а для вопроса «откуда люди приходят» хватает хоста.
 */
export function arrivalFacts(href: string, referrer: string): Record<string, string> {
  const facts: Record<string, string> = {};
  try {
    const url = new URL(href);
    for (const field of UTM_FIELDS) {
      const value = url.searchParams.get(field);
      if (value && /^[\p{L}\p{N} ._-]{1,64}$/u.test(value)) facts[field] = value;
    }
    facts.landing_path = url.pathname.slice(0, 128);
  } catch { /* адрес без разбора — просто нет меток */ }
  try {
    const from = new URL(referrer);
    if (from.hostname && from.hostname !== new URL(href).hostname) {
      facts.referrer_host = from.hostname.toLowerCase().slice(0, 128);
    }
  } catch { /* прямой заход */ }
  return facts;
}

/** Четверти Акта 0: 0 — не начинал, 4 — досмотрел до маршрута. */
export function filmQuartile(scrollY: number, viewportHeight: number, endVh: number): number {
  if (!(endVh > 0) || !(viewportHeight > 0)) return 0;
  const progress = scrollY / viewportHeight / endVh;
  if (!Number.isFinite(progress) || progress <= 0) return 0;
  return Math.max(0, Math.min(4, Math.floor(progress * 4)));
}

/*
 * Строка видеокарты. Нужна ровно для одного: понять, у кого именно не
 * отрисовался мост. Контекст берётся крошечный и одноразовый, и только тогда,
 * когда о сбое действительно сообщают.
 */
function rendererName(view: View): string | undefined {
  try {
    const canvas = view.document.createElement("canvas");
    const gl = (canvas.getContext("webgl")
      ?? canvas.getContext("experimental-webgl")) as WebGLRenderingContext | null;
    if (!gl) return undefined;
    const info = gl.getExtension("WEBGL_debug_renderer_info");
    const name = info
      ? gl.getParameter(info.UNMASKED_RENDERER_WEBGL) as unknown
      : gl.getParameter(gl.RENDERER) as unknown;
    gl.getExtension("WEBGL_lose_context")?.loseContext();
    return typeof name === "string" && name.trim() ? name.trim().slice(0, 128) : undefined;
  } catch { return undefined; }
}

const SECOND_STEPS = [600, 300, 120, 60, 30, 10];

function roundSeconds(seconds: number): number {
  return SECOND_STEPS.find(step => seconds >= step) ?? 0;
}

export interface CollectorPorts {
  view: View;
  transport: Transport;
  contour: "ru" | "com";
  /** Возвращает опознаватели, когда согласие есть, и `null`, когда его нет. */
  identity(): { visitId: string; sessionId: string; returning: boolean } | null;
}

export interface Collector {
  /** Device diagnostics are emitted only while analytics consent is active. */
  failure(name: "boot_fallback" | "asset_failed" | "webgl_lost", props: Record<string, string | number>): void;
  /** Вызывается, когда согласие только что получено: досылает начало визита. */
  start(): void;
  stop(): void;
}

export function createCollector(ports: CollectorPorts): Collector {
  const { view, transport, contour } = ports;
  const document = view.document;
  const openedAt = view.performance?.now?.() ?? Date.now();

  let deepestRank = 0;
  let deepestSection = "film";
  let reachedPricing = false;
  let reachedInstall = false;
  let visitSent = false;
  let lastDepth = "";
  let filmMaximum = 0;
  let currentSection = "film";
  let activeMs = 0;
  let activeFrom = document.visibilityState === "hidden" ? null : openedAt;
  const seenPlans = new Set<string>();
  const failures = new Set<string>();
  let readySent=false, bridgeExpectedSince=0;
  let language = document.documentElement.lang || "en";
  let renderer: string | undefined;
  const listeners: Array<() => void> = [];

  const device = (): Device => deviceKind(view.innerWidth);

  const emit = (name: string, props: Record<string, string | number | boolean>): void => {
    const identity = ports.identity();
    if (!identity) return;
    transport.push({
      name,
      visit_id: identity.visitId,
      session_id: identity.sessionId,
      props,
    } satisfies AnalyticsEvent);
  };

  /* ── Технический уровень ──────────────────────────────────────────────── */

  const failure: Collector["failure"] = (name, props) => {
    if (!ports.identity()) return;
    const key = `${name}:${props.asset ?? ''}:${props.stage ?? ''}`;
    if (failures.has(key) || failures.size >= 12) return;
    failures.add(key);
    renderer ??= rendererName(view);
    transport.push({
      name,
      props: {
        ...props,
        browser: browserFamily(view.navigator.userAgent),
        device: device(),
        release: import.meta.env.VITE_BB_SITE_VERSION ?? "unversioned",
        ...(renderer ? { renderer } : {}),
      },
    });
  };

  /* ── Глубина маршрута ─────────────────────────────────────────────────── */

  const order: readonly string[] = ['film', ...SECTION_IDS];
  const noteSection = (id: string): void => {
    const rank = order.indexOf(id);
    if (rank < 0) return;
    currentSection = id;
    if (rank > deepestRank) { deepestRank = rank; deepestSection = id; }
    if (id === 'pricing') reachedPricing = true;
    if (id === 'install') reachedInstall = true;
  };
  const visible = (node: HTMLElement): boolean => {
    if (node.hidden || node.closest('[hidden],[inert],[data-cinema-hidden="true"]')) return false;
    const style = view.getComputedStyle(node);
    if (style.visibility === 'hidden' || style.display === 'none' || Number(style.opacity) === 0) return false;
    const box = node.getBoundingClientRect();
    const height = Math.max(0, Math.min(view.innerHeight, box.bottom) - Math.max(0, box.top));
    return box.height > 0 && box.width > 0 && height >= Math.min(box.height, view.innerHeight) * 0.5;
  };
  const sample = (): void => {
    if (document.visibilityState === 'hidden' || !ports.identity()) return;
    const route = document.querySelector<HTMLElement>('[data-marketing-route]');
    const chapter = route?.dataset.cinemaChapter;
    if(view.__BB_CINEMA__?.transport.completed.has(0)) filmMaximum=4;
    if (route?.dataset.cinema === 'true' && chapter) {
      if (chapter === 'film') {
        currentSection = 'film';
        const duration = CHAPTERS.find(item => item.id === 'film')!.duration;
        filmMaximum = Math.max(filmMaximum, Math.min(4, Math.floor(Number(route.dataset.cinemaElapsed || 0) / duration * 4)));
      } else if (chapter !== 'intro' && route.dataset.cinemaPhase !== 'transition') noteSection(chapter);
      // A jump straight to pricing is not proof that the film was watched.
    } else {
      const score = (view as unknown as { BB_FILM_SCORE?: { endVh?: number } }).BB_FILM_SCORE;
      filmMaximum = Math.max(filmMaximum, filmQuartile(view.scrollY, view.innerHeight, score?.endVh ?? 0));
      for (const node of document.querySelectorAll<HTMLElement>('[data-section-id]')) if (visible(node)) noteSection(node.dataset.sectionId!);
    }
    for (const node of document.querySelectorAll<HTMLElement>('[data-offer-plan]')) {
      const months = node.dataset.offerPlan!;
      if (!seenPlans.has(months) && visible(node)) {
        seenPlans.add(months);
        emit('plan_interest', { action: 'view', months, device: device() });
      }
    }
    const flags = document.documentElement.dataset;
    if(!readySent) {
      const ready=view.performance.getEntriesByName('bb-site-ready')[0];
      if(ready) {
        readySent=true;
        transport.push({name:'site_ready',props:{ready_ms:[1000,3000,5000,10000,20000,60000].find(limit=>ready.startTime<=limit)??60000,
          browser:browserFamily(view.navigator.userAgent),device:device(),release:import.meta.env.VITE_BB_SITE_VERSION ?? 'unversioned'}});
      }
    }
    if (flags.webglFallback === 'true') failure('boot_fallback', {reason: flags.marketingBootstrapFallback === 'true' ? 'bootstrap' : 'webgl'});
    if (flags.bbBridgeAsset === 'failed') failure('asset_failed', {asset:'bridge', stage:'load', attempts:Number(flags.bbBridgeAttempts || 1)});
    if (flags.bbCubeAsset === 'failed') failure('asset_failed', {asset:'cube',stage:'load',attempts:1});
    if(flags.bbBridgeExpected==='true' && flags.bbBridgeRendered!=='true') {
      bridgeExpectedSince ||= view.performance.now();
      if(view.performance.now()-bridgeExpectedSince>5000) failure('asset_failed',{asset:'bridge',stage:'render',attempts:1});
    } else bridgeExpectedSince=0;
  };

  const sendVisit = (): void => {
    const identity = ports.identity();
    if (visitSent || !identity) return;
    visitSent = true;
    emit("visit", {
      contour,
      lang: language,
      ...arrivalFacts(view.location.href, document.referrer),
      viewport: `${view.innerWidth}x${view.innerHeight}`,
      dpr: Math.round((view.devicePixelRatio || 1) * 10) / 10,
      returning: identity.returning,
      device: device(),
      release: import.meta.env.VITE_BB_SITE_VERSION ?? "unversioned",
    });
  };

  const sendDepth = (): void => {
    if (!ports.identity()) return;
    sample();
    const now = view.performance?.now?.() ?? Date.now();
    const seconds = Math.floor((activeMs + (activeFrom === null ? 0 : now - activeFrom)) / 1000);
    const signature = JSON.stringify([filmMaximum, deepestSection, roundSeconds(seconds), reachedPricing, reachedInstall]);
    if (signature === lastDepth) { transport.flush({beacon:true}); return; }
    lastDepth = signature;
    emit("route_depth", {
      film_q: filmMaximum,
      section: deepestSection,
      seconds: roundSeconds(seconds),
      reached_pricing: reachedPricing,
      reached_install: reachedInstall,
      device: device(),
    });
    transport.flush({ beacon: true });
  };

  /* ── Нажатия ──────────────────────────────────────────────────────────── */

  const CTA_BY_ID: Record<string, string> = { cta: "hero", finalCta: "final", signin: "signin" };

  const onClick = (event: Event): void => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const cta = target.closest<HTMLElement>("#cta,#finalCta,#signin,.logo,[data-cinema-link]");
    if (cta) {
      const named = CTA_BY_ID[cta.id]
        ?? (cta.classList.contains("logo") ? "logo" : null)
        ?? (cta.dataset.cinemaLink ? `nav_${cta.dataset.cinemaLink}` : null);
      if (named) emit("cta_click", { cta: named, section: currentSection, device: device() });
    }

    const plan = target.closest<HTMLButtonElement>("[data-offer-select]");
    if (plan?.dataset.offerSelect && !plan.disabled) {
      emit("plan_interest", { action: "select", months: plan.dataset.offerSelect, device: device() });
    }

    const download = target.closest<HTMLElement>("[data-offer-download]");
    if (download?.dataset.offerDownload) {
      emit("download_click", {
        target: download.dataset.offerDownload,
        // Отключённая кнопка нажатий не порождает вовсе; поле останется
        // честным в тот день, когда кнопки станут рабочими.
        available: !(download as HTMLButtonElement).disabled,
        device: device(),
      });
    }

    const legal = target.closest<HTMLAnchorElement>('a[href="/privacy"],a[href^="/privacy?"],a[href^="/privacy#"],a[href="/terms"],a[href^="/terms?"],a[href^="/terms#"],[data-bb-consent-more]');
    if (legal) {
      const doc = legal.hasAttribute("data-bb-consent-more") ? "consent"
        : legal.getAttribute("href")?.includes("terms") ? "terms" : "privacy";
      emit("legal_open", { doc, device: device() });
    }

    const social = target.closest<HTMLElement>("[data-offer-social]");
    if (social?.dataset.offerSocial) {
      const network = social.dataset.offerSocial === "twitter" ? "x" : social.dataset.offerSocial;
      emit("social_click", { network, device: device() });
    }
  };

  /* ── Смена языка ──────────────────────────────────────────────────────── */

  const onLocaleChange = (): void => {
    const next = document.documentElement.lang || "en";
    if (next !== language) {
      emit("lang_change", { from: language, to: next, device: device() });
      language = next;
    }
  };

  /*
   * Уход со страницы. `visibilitychange` — единственное событие, которое
   * браузеры телефонов действительно доставляют при закрытии вкладки;
   * `pagehide` оставлен для тех, кто не доставляет и его.
   */
  const onHide = (): void => {
    const now = view.performance?.now?.() ?? Date.now();
    if (document.visibilityState === 'hidden') {
      if (activeFrom !== null) activeMs += now - activeFrom;
      activeFrom = null;
      sendDepth();
    } else { activeFrom = now; sample(); }
  };
  const onPageHide = (): void => sendDepth();

  const listen = <K extends keyof WindowEventMap>(
    target: Window | Document, type: K | string, handler: EventListener, options?: AddEventListenerOptions
  ): void => {
    target.addEventListener(type, handler, options);
    listeners.push(() => target.removeEventListener(type, handler, options));
  };

  listen(document, 'webglcontextlost', (() => failure('webgl_lost', {section:currentSection})) as EventListener, {capture:true});
  listen(document, 'error', ((event: Event) => {
    if (event.target instanceof view.HTMLVideoElement) failure('asset_failed', {asset:'video',stage:'load',attempts:1});
  }) as EventListener, {capture:true});
  listen(document, "click", onClick, { capture: true, passive: true });
  listen(view, "bb:localechange", onLocaleChange);
  listen(document, "visibilitychange", onHide);
  listen(view, "pagehide", onPageHide);

  const sampler = view.setInterval(sample, 500);
  let frameRequest=0, previousFrame=0, frameCount=0, frameMs=0;
  const measureFrames=(now:number):void=>{
    if(document.visibilityState==='hidden') {previousFrame=0;}
    else {
      if(previousFrame>0 && now-previousFrame<1000) {frameCount++;frameMs+=now-previousFrame;}
      previousFrame=now;
    }
    if(frameMs>=5000) {
      const fps=frameCount*1000/frameMs;
      emit('site_performance',{frame_band:fps<24?'under_24':fps<45?'24_44':'45_plus',device:device(),release:import.meta.env.VITE_BB_SITE_VERSION ?? 'unversioned'});
      frameRequest=0;
    } else frameRequest=view.requestAnimationFrame(measureFrames);
  };
  frameRequest=view.requestAnimationFrame(measureFrames);
  sample();
  return {
    failure,
    start: sendVisit,
    stop() {
      view.clearInterval(sampler);
      if(frameRequest)view.cancelAnimationFrame(frameRequest);
      for (const off of listeners.splice(0)) off();
    },
  };
}
